import {
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Link, Navigate, useLocation, useParams } from 'react-router';
import { useTypedSelector } from '../reducers';
import { ScreenWrap, HeaderBreadcrumbs } from '.';
import { Api } from '../api';
import { healthQuestionnaire } from '../questionnaires/healthQuestionnaire';
import { onboardingQuestionnaire } from '../questionnaires/onboardingQuestionnaire';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { Model } from '../models';
import logError from '../util/log-error';

type QuestionResponse = {
  question: string;
  answer: string | string[];
  questionSlug?: string;
};

export default function QuestionnaireResponses() {
  const { id } = useParams();
  const { viewMode } = useTypedSelector((state) => state.auth);
  const location = useLocation();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1];
  const currentTab =
    lastPart === 'onboarding' || lastPart === 'pre-health-coach'
      ? lastPart
      : null;

  const statsApi = Api.UserStats.useReadItemStats(id!);

  if (location.pathname === `/users/${id}/questionnaire-responses`) {
    return (
      <Navigate
        to={`/users/${id}/questionnaire-responses/onboarding`}
        replace
      />
    );
  }

  const isAdmin = viewMode === 'admin';
  const basePath = '/users';

  return (
    <ScreenWrap>
      <Stack spacing={3}>
        <Box>
          <HeaderBreadcrumbs
            title="Questionnaire Responses"
            items={[
              {
                name: isAdmin ? 'App Users' : 'Your Clients',
                url: basePath,
              },
              {
                name:
                  statsApi.data?.user.name ?? statsApi.data?.user.email ?? '',
                url: `${basePath}/${id}/user-dashboard`,
              },
              {
                name: 'Questionnaire Responses',
              },
            ]}
          />
        </Box>

        <Tabs
          value={currentTab || 'onboarding'}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="onboarding"
            label="Onboarding"
            component={Link}
            to={`/users/${id}/questionnaire-responses/onboarding`}
          />
          <Tab
            value="pre-health-coach"
            label="Pre-Health Coach"
            component={Link}
            to={`/users/${id}/questionnaire-responses/pre-health-coach`}
          />
        </Tabs>
        {currentTab === 'onboarding' && <OnboardingResponses id={id!} />}
        {currentTab === 'pre-health-coach' && (
          <PreHealthCoachResponses id={id!} />
        )}
      </Stack>
    </ScreenWrap>
  );
}

function OnboardingResponses({ id }: { id: string }) {
  const api = Api.UserQuestionnaire.useReadItemQuestionnairesType(
    id,
    'onboarding',
  );
  const statsApi = Api.UserStats.useReadItemStats(id);
  const symptomsApi = Api.Symptom.useReadCollection();

  const error = api.error || statsApi.error || symptomsApi.error;

  useEffect(() => {
    if (error) logError(error, 'OnboardingResponses');
  }, [error]);

  if (error)
    return (
      <Card>
        <CardContent>
          <Typography variant="body2">Failed to load responses.</Typography>
        </CardContent>
      </Card>
    );

  if (!api.data || !statsApi.data || !symptomsApi.data)
    return (
      <Card>
        <CardContent>
          <Typography variant="body2">Loading...</Typography>
        </CardContent>
      </Card>
    );

  const responsesRaw = api.data.responses || [];
  const user = statsApi.data.user;
  const allSymptoms = symptomsApi.data.member || [];

  const normalized = responsesRaw.map((r): QuestionResponse => {
    if (Array.isArray(r)) {
      const [question, answer] = r;
      return { question, answer };
    }
    return r as QuestionResponse;
  });

  const processQ14DetailAnswer = (answer: Record<string, string>): string[] => {
    return Object.entries(answer).map(([symptomIri, duration]) => {
      const symptom = allSymptoms.find((s) => s['@id'] === symptomIri);
      const symptomName = symptom?.name || `Unknown symptom`;
      const durationLabel = Model.User.getSymptomDurationLabel(duration);
      return `${symptomName}: ${durationLabel}`;
    });
  };

  const getUserAnswer = (onboardingField: string): string | null => {
    switch (onboardingField) {
      case 'name':
        return user.name;
      case 'gender':
        return user.gender;
      case 'dob':
        return user.dob ? format(new Date(user.dob), 'dd/MM/yyyy') : null;
      case 'ethnicity':
        return user.ethnicity;
      case 'height':
        return user.height ? `${user.height} cm` : null;
      case 'weight':
        return user.weight ? `${user.weight} kg` : null;
      case 'country':
        return user.country ? Model.User.getCountryLabel(user.country) : null;
      case 'pregnancyIntent':
        return user.pregnancyIntent;
      case 'generalHealthConditions':
        return user.generalHealthConditions &&
          user.generalHealthConditions.length > 0
          ? (user.generalHealthConditions as unknown as string[]).join(', ')
          : null;
      case 'observedSymptomForConditions':
        return user.observedSymptomForConditions &&
          user.observedSymptomForConditions.length > 0
          ? user.observedSymptomForConditions.length.toString()
          : null;
      case 'symptomsAroundPeriod':
        return user.symptomsAroundPeriod && user.symptomsAroundPeriod.length > 0
          ? user.symptomsAroundPeriod.map((s) => s.name).join(', ')
          : null;
      case 'symptomsAfterMeal':
        return user.symptomsAfterMeal && user.symptomsAfterMeal.length > 0
          ? user.symptomsAfterMeal.map((s) => s.name).join(', ')
          : null;
      case 'recipePreferences':
        return user.recipePreferences && user.recipePreferences.length > 0
          ? user.recipePreferences.map((r) => r.name).join(', ')
          : null;
      default:
        return null;
    }
  };

  const groupedResponses = onboardingQuestionnaire.sections
    .map((section) => {
      const sectionResponses = [] as QuestionResponse[];

      section.questions.forEach((question) => {
        if (question.type === 'info') {
          return;
        }

        const apiResponse = normalized.find(
          (response) => response.question === question.question,
        );

        const questionWithField = question as { onboardingField?: string };

        if (questionWithField.onboardingField) {
          const answer = getUserAnswer(questionWithField.onboardingField);
          if (answer !== null) {
            sectionResponses.push({
              question: question.question ?? '',
              answer: answer,
              questionSlug: question.id,
            });
          } else if (apiResponse) {
            sectionResponses.push({
              ...apiResponse,
              questionSlug: question.id,
            });
          }
        } else if (apiResponse) {
          if (question.id === 'q14') return;
          if (
            question.id === 'q14-detail' &&
            typeof apiResponse.answer === 'object' &&
            apiResponse.answer !== null &&
            !Array.isArray(apiResponse.answer)
          ) {
            sectionResponses.push({
              question: apiResponse.question,
              answer: processQ14DetailAnswer(
                apiResponse.answer as Record<string, string>,
              ),
              questionSlug: question.id,
            });
          } else {
            sectionResponses.push({
              ...apiResponse,
              questionSlug: question.id,
            });
          }
        }

        if (question.id === 'q8') {
          if (Array.isArray(user.diagnoses) && user.diagnoses.length > 0) {
            const q8Response = sectionResponses.find(
              (r) => r.questionSlug === 'q8',
            );
            if (!q8Response) {
              sectionResponses.push({
                question:
                  'Have you ever been diagnosed with any of the following?',
                answer: user.diagnoses
                  .map((d) => Model.User.getDiagnosisLabel(d))
                  .join(', '),
                questionSlug: 'q8',
              });
            }
            user.diagnoses.forEach((diagnosis, index) => {
              const diagnosisLabel = Model.User.getDiagnosisLabel(diagnosis);
              sectionResponses.push({
                question: `Who provided you with a formal diagnosis for ${diagnosisLabel}?`,
                answer: diagnosis.diagnosedBy,
                questionSlug: `q10-${index}`,
              });
              sectionResponses.push({
                question: `At what age were you diagnosed with ${diagnosisLabel}?`,
                answer: `${diagnosis.diagnosedAge} years old`,
                questionSlug: `q11-${index}`,
              });
              sectionResponses.push({
                question: `What management were you provided for ${diagnosisLabel}?`,
                answer: (diagnosis.managements || []).join(', '),
                questionSlug: `q12-${index}`,
              });
            });
          }
        }

        if (question.id === 'q20') {
          if (
            Array.isArray(user.contraceptions) &&
            user.contraceptions.length > 0
          ) {
            const q20Response = sectionResponses.find(
              (r) => r.questionSlug === 'q20',
            );
            if (!q20Response) {
              sectionResponses.push({
                question:
                  'What forms of contraception have you used or are you currently using?',
                answer: user.contraceptions
                  .map((c) => {
                    const label = Model.User.getContraceptionLabel(c.type);
                    return `${label}${c.currentlyUsing ? ' (currently using)' : ''}`;
                  })
                  .join(', '),
                questionSlug: 'q20',
              });
            }

            user.contraceptions.forEach((c, index) => {
              const contraceptionLabel = Model.User.getContraceptionLabel(
                c.type,
              );
              if (c.usageLength) {
                sectionResponses.push({
                  question: `How long have you been using ${contraceptionLabel}?`,
                  answer: `${c.usageLength} months`,
                  questionSlug: `q21-${index}`,
                });
              }
              if (c.stoppedUsingAt) {
                sectionResponses.push({
                  question: `When did you stop using ${contraceptionLabel}?`,
                  answer: new Date(c.stoppedUsingAt).toLocaleDateString(),
                  questionSlug: `q22-${index}`,
                });
              }
              if (Array.isArray(c.symptoms) && c.symptoms.length > 0) {
                sectionResponses.push({
                  question: `Did ${contraceptionLabel} trigger any symptoms?`,
                  answer: c.symptoms.map((s) => s.name).join(', '),
                  questionSlug: `q23-${index}`,
                });
              }
            });
          }
        }
      });

      const uniqueResponses = sectionResponses.filter(
        (response, index, array) => {
          const key = (val: string | string[]) =>
            typeof val === 'string' ? val : JSON.stringify(val);
          const respKey = `${response.question}::${key(response.answer)}`;
          const firstIndex = array.findIndex((r) => {
            const compKey = `${r.question}::${key(r.answer)}`;
            return compKey === respKey;
          });
          return firstIndex === index;
        },
      );

      return { title: section.title, responses: uniqueResponses };
    })
    .filter((section) => section.responses.length > 0);

  return (
    <Card>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Section
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '40%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Question
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '40%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Answer
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedResponses.map((group) =>
              group.responses.map((row, index) => {
                const isFirstInSection = index === 0;
                return (
                  <TableRow key={`${group.title}-${index}`}>
                    <TableCell>
                      <Typography variant="body2">
                        {isFirstInSection ? group.title : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.question}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {Array.isArray(row.answer)
                          ? row.answer.join(', ')
                          : String(row.answer ?? '')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function PreHealthCoachResponses({ id }: { id: string }) {
  const api = Api.UserQuestionnaire.useReadItemQuestionnairesType(
    id,
    'before_appointment',
  );

  useEffect(() => {
    if (api.error) logError(api.error, 'PreHealthCoachResponses');
  }, [api.error]);

  if (api.error)
    return (
      <Card>
        <CardContent>
          <Typography variant="body2">Failed to load responses.</Typography>
        </CardContent>
      </Card>
    );

  if (!api.data)
    return (
      <Card>
        <CardContent>
          <Typography variant="body2">Loading...</Typography>
        </CardContent>
      </Card>
    );

  const rawResponses = api.data.responses || [];

  const processQ42DetailAnswer = (answer: Record<string, string>): string[] => {
    return Object.entries(answer).map(([foodType, frequency]) => {
      const frequencyLabel =
        Model.Questionnaire.getFoodFrequencyLabel(frequency);
      return `${foodType}: ${frequencyLabel}`;
    });
  };

  const normalized = rawResponses.map((r): QuestionResponse => {
    if (Array.isArray(r)) {
      const [question, answer] = r;
      return { question, answer };
    }

    const response = r as QuestionResponse;

    if (
      response.question === 'How often do you eat these foods?' &&
      typeof response.answer === 'object' &&
      response.answer !== null &&
      !Array.isArray(response.answer)
    ) {
      return {
        question: response.question,
        answer: processQ42DetailAnswer(
          response.answer as Record<string, string>,
        ),
      };
    }

    return response;
  });

  const groupedResponses = healthQuestionnaire.sections
    .map((section) => ({
      title: section.title,
      responses: normalized.filter((response) =>
        section.questions.some((q) => q.question === response.question),
      ),
    }))
    .filter((section) => section.responses.length > 0);

  return (
    <Card>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Section
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '40%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Question
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '40%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Answer
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedResponses.map((group) =>
              group.responses.map((row, index) => {
                const isFirstInSection = index === 0;
                return (
                  <TableRow key={`${group.title}-${index}`}>
                    <TableCell>
                      <Typography variant="body2">
                        {isFirstInSection ? group.title : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.question}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {Array.isArray(row.answer)
                          ? row.answer.join(', ')
                          : String(row.answer ?? '')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
